import React, { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import {
  Sun, Moon, Globe, Cpu, Server, Zap, ArrowRight, ExternalLink,
  Menu, X, Mail, Code2, Database, Cloud, Layers, GitBranch,
  Shield, CheckCircle2, Send, Sparkles, Workflow, Rocket, Search,
  PenTool, Hammer, Brain, Smartphone, MessageSquare, TrendingUp,
  Share2, BarChart3, Lightbulb, Plug, Star, ChevronDown, Phone, Quote,
  MessageCircle, Linkedin, Instagram
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRAND = "NextGen";
const BRAND_FULL = "NextGen Web Solutions";
const DOMAIN = "next-gen.in";
const EMAIL = "admin@next-gen.in";
const PHONE = "+918679900841";
const PHONE_DISPLAY = "+91 86799 00841";
const WHATSAPP_URL = `https://wa.me/918679900841?text=${encodeURIComponent("Hi NextGen — I'd like to discuss a project.")}`;
const LINKEDIN_URL = "https://www.linkedin.com/company/nextgen1245/";
const INSTAGRAM_URL = "https://www.instagram.com/_next_gen_solution_/";

/* ───────── LOGO ───────── */
const Logo = ({ size = 36 }) => (
  <span
    className="ng-logo"
    style={{ width: size, height: size }}
    aria-label="NextGen logo"
  >
    <svg viewBox="0 0 120 140" xmlns="http://www.w3.org/2000/svg" width={size} height={size}>
      {/* darker outer chevron */}
      <path d="M14 8 L60 70 L14 132 L36 132 L82 70 L36 8 Z"
            fill="#0d8a8a" stroke="#062c2c" strokeWidth="2" strokeLinejoin="miter" />
      {/* brighter inner chevron */}
      <path d="M40 8 L86 70 L40 132 L60 132 L106 70 L60 8 Z"
            fill="#3ddbcd" stroke="#062c2c" strokeWidth="2" strokeLinejoin="miter" />
    </svg>
  </span>
);

/* ───────── THEME (auto by hour, override-able) ───────── */
const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("ng-theme") : null;
    if (stored === "day" || stored === "night") return stored;
    const h = new Date().getHours();
    return h >= 6 && h < 18 ? "day" : "night";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("theme-night", theme === "night");
    document.documentElement.classList.toggle("theme-day", theme === "day");
  }, [theme]);

  // Periodic re-check: if user hasn't manually overridden, follow the clock.
  useEffect(() => {
    const tick = () => {
      if (localStorage.getItem("ng-theme")) return;
      const h = new Date().getHours();
      const wanted = h >= 6 && h < 18 ? "day" : "night";
      setTheme(prev => (prev === wanted ? prev : wanted));
    };
    const id = setInterval(tick, 60_000); // check every minute
    return () => clearInterval(id);
  }, []);

  const toggle = useCallback(() => {
    setTheme(prev => {
      const next = prev === "day" ? "night" : "day";
      localStorage.setItem("ng-theme", next);
      return next;
    });
  }, []);

  return { theme, toggle };
};

/* ───────── SKY ───────── */
const STAR_FIELD = Array.from({ length: 150 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  s: Math.random() * 1.8 + 0.5,
  d: Math.random() * 4,
  dur: Math.random() * 3 + 2,
}));

const Sky = () => {
  const stars = STAR_FIELD;

  return (
    <div className="sky" aria-hidden="true">
      <div className="sky-day" />
      <div className="sky-night" />
      <div className="sun" />
      <div className="moon" />
      <div className="cloud cloud-a" />
      <div className="cloud cloud-b" />
      <div className="cloud cloud-c" />
      <div className="cloud cloud-d" />
      <div className="cloud cloud-e" />
      <div className="stars-layer">
        {stars.map(s => (
          <span key={s.id} className="star"
            style={{
              left: `${s.x}%`, top: `${s.y}%`,
              width: `${s.s}px`, height: `${s.s}px`,
              animationDelay: `${s.d}s`, animationDuration: `${s.dur}s`,
            }} />
        ))}
        <span className="shooting-star" style={{ top: "20%", left: "85%", animationDelay: "2s" }} />
        <span className="shooting-star" style={{ top: "50%", left: "70%", animationDelay: "9s" }} />
        <span className="shooting-star" style={{ top: "35%", left: "60%", animationDelay: "14s" }} />
      </div>
    </div>
  );
};

/* ───────── REVEAL ───────── */
const useReveal = () => {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ───────── NAV ───────── */
const Nav = ({ theme, toggle }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { name: "Services", href: "#services" },
    { name: "Work", href: "#portfolio" },
    { name: "Capabilities", href: "#capabilities" },
    { name: "Process", href: "#process" },
    { name: "FAQ", href: "#faq" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <>
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "py-2 sm:py-3" : "py-4 sm:py-5"}`} data-testid="main-nav">
        <div className={`wrap flex items-center justify-between gap-3 ${scrolled ? "glass-strong rounded-full px-3 sm:px-5 py-1.5 sm:py-2 mx-3 sm:mx-4 lg:mx-auto" : ""}`}
             style={scrolled ? { maxWidth: "1140px" } : {}}>
          <a href="#hero" className="flex items-center gap-2 sm:gap-2.5 min-w-0" data-testid="logo-link">
            <Logo size={32} />
            <span className="font-semibold text-base sm:text-lg tracking-tight truncate" style={{ color: "var(--ink)" }}>{BRAND}</span>
          </a>

          <div className="hidden lg:flex items-center gap-7">
            {links.map(l => (
              <a key={l.name} href={l.href}
                 className="text-sm font-medium transition-colors hover:opacity-100"
                 style={{ color: "var(--ink-soft)" }}
                 data-testid={`nav-${l.name.toLowerCase()}`}>
                {l.name}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button onClick={toggle} className="theme-toggle" aria-label="Toggle day/night theme" data-testid="theme-toggle-btn">
              <span className="theme-toggle-knob">
                {theme === "day" ? <Sun size={14} /> : <Moon size={14} />}
              </span>
            </button>

            <a href="#contact" className="hidden lg:inline-flex btn-primary text-sm" data-testid="nav-cta-btn">
              Start Project <ArrowRight size={16} />
            </a>

            <button className="lg:hidden p-2 -mr-1" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"
                    aria-label="Open menu"
                    style={{ color: "var(--ink)" }}>
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {open && (
        <div className="mobile-overlay lg:hidden flex flex-col items-center justify-center gap-7" data-testid="mobile-menu-overlay">
          <button onClick={() => setOpen(false)} className="absolute top-6 right-6 p-2" style={{ color: "var(--ink)" }}>
            <X size={26} />
          </button>
          {links.map(l => (
            <a key={l.name} href={l.href} onClick={() => setOpen(false)}
               className="text-3xl font-semibold" style={{ color: "var(--ink)" }}>
              {l.name}
            </a>
          ))}
          <a href="#contact" onClick={() => setOpen(false)} className="btn-primary mt-4">
            Start Project <ArrowRight size={18} />
          </a>
        </div>
      )}
    </>
  );
};

/* ───────── HERO ───────── */
const Hero = () => (
  <section id="hero" className="relative min-h-[100vh] flex items-center pt-28 pb-24">
    <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
    <div className="wrap relative z-10 text-center">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-8 reveal">
        <span className="badge-dot" />
        <span className="text-xs font-medium tracking-wide" style={{ color: "var(--ink-soft)" }}>
          Now booking Q2 2026 · AI · Web · Automation · Servers
        </span>
      </div>

      <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-bold leading-[1.02] tracking-tight reveal">
        Engineering the
        <br />
        <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>next generation</span>
        <br />
        of the web.
      </h1>

      <p className="mt-8 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed reveal"
         style={{ color: "var(--ink-soft)", transitionDelay: "120ms" }}>
        {BRAND_FULL} ships AI products, web & mobile apps, automation systems, performance marketing
        and dedicated server infrastructure — for ambitious teams.
      </p>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 reveal" style={{ transitionDelay: "240ms" }}>
        <a href="#portfolio" className="btn-primary" data-testid="hero-portfolio-btn">
          View Our Work <ArrowRight size={18} />
        </a>
        <a href="#contact" className="btn-ghost" data-testid="hero-contact-btn">
          Book a Free Consult
        </a>
      </div>

      <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 max-w-3xl mx-auto gap-6 sm:gap-8 reveal" style={{ transitionDelay: "320ms" }}>
        {[
          { n: "50+", l: "Projects shipped" },
          { n: "12+", l: "Industries served" },
          { n: "5+", l: "Years engineering" },
          { n: "99.9%", l: "Uptime SLA" },
        ].map(s => (
          <div key={s.l} className="text-center">
            <div className="text-3xl sm:text-4xl font-bold gradient-text">{s.n}</div>
            <div className="text-xs sm:text-sm mt-1.5" style={{ color: "var(--ink-mute)" }}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ───────── TRUST MARQUEE ───────── */
const Trust = () => {
  const stack = ["React", "Next.js", "Node.js", "Python", "FastAPI", "MongoDB", "PostgreSQL", "AWS", "Docker", "Kubernetes", "TypeScript", "OpenAI", "LangChain", "n8n", "Power BI"];
  return (
    <section className="py-16" style={{ borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
      <div className="wrap mb-8">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: "var(--ink-mute)" }}>
          Built with a battle-tested modern stack
        </p>
      </div>
      <div className="overflow-hidden">
        <div className="marquee-track">
          {[...stack, ...stack].map((s, i) => (
            <div key={i} className="text-2xl font-semibold opacity-50 hover:opacity-100 transition-opacity whitespace-nowrap"
                 style={{ color: "var(--ink)" }}>
              {s}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────── SERVICES (Premium interactive split layout) ───────── */
const Services = () => {
  const items = [
    { num: "01", icon: Brain, title: "AI Development & Automation", short: "Intelligent agents, chatbots & generative-AI workflows.",
      desc: "We design, build and deploy production-grade AI systems — custom GPT/Claude/Gemini agents, RAG pipelines, computer vision and intelligent automation that learns from your business and gets better over time.",
      pills: ["Custom Chatbots", "AI Agents", "RAG / Vector DBs", "NLP", "Computer Vision", "Workflow Automation"],
      kpi: [{ k: "10×", v: "Faster ops" }, { k: "60%", v: "Cost saved" }] },
    { num: "02", icon: Globe, title: "Web & Mobile Development", short: "Pixel-perfect web & cross-platform mobile apps.",
      desc: "Bespoke web and native-feeling mobile applications engineered with React, Next.js and React Native. Server-rendered for speed, accessible by default, and instrumented for growth from day one.",
      pills: ["React", "Next.js", "React Native", "TypeScript", "PWA", "SSR / Edge"],
      kpi: [{ k: "<1.2s", v: "LCP target" }, { k: "100", v: "Lighthouse" }] },
    { num: "03", icon: Lightbulb, title: "Generative AI Consulting", short: "Strategy, model selection & responsible deployment.",
      desc: "Senior advisory on where AI moves the needle in your business — model selection, vendor evaluation, build-vs-buy, governance and a phased roadmap that ships value in weeks, not quarters.",
      pills: ["AI Strategy", "Model Selection", "Build vs Buy", "Governance", "Training", "Future-proofing"],
      kpi: [{ k: "2 wks", v: "First value" }, { k: "100%", v: "IP retained" }] },
    { num: "04", icon: Share2, title: "Social Media Management", short: "Brand presence, content & community at scale.",
      desc: "Original content, brand voice and analytics-led community growth across Instagram, LinkedIn, X and YouTube. We treat social as a measurable growth channel, not a vanity exercise.",
      pills: ["Content Strategy", "Reels & Shorts", "Community", "Brand Voice", "Analytics", "Reporting"],
      kpi: [{ k: "3×", v: "Avg engagement" }, { k: "+85%", v: "Follower growth" }] },
    { num: "05", icon: TrendingUp, title: "Performance Marketing", short: "Paid ads engineered for ROI, not vanity metrics.",
      desc: "Full-funnel performance — Meta, Google, LinkedIn — with conversion-rate optimisation, rigorous A/B testing and weekly reporting against the metrics that actually move revenue.",
      pills: ["Meta Ads", "Google Ads", "LinkedIn", "CRO", "A/B Testing", "Attribution"],
      kpi: [{ k: "4–7×", v: "ROAS range" }, { k: "−42%", v: "CAC" }] },
    { num: "06", icon: Search, title: "SEO & GMB", short: "Dominate organic + local search.",
      desc: "Technical SEO, content engines, answer-engine optimisation and tightly managed Google Business Profiles. Built to compound — most clients see meaningful traffic growth from month 3.",
      pills: ["Technical SEO", "Content", "Local SEO", "GMB", "Schema", "AEO / GEO"],
      kpi: [{ k: "3×", v: "Organic traffic" }, { k: "Top 3", v: "Local pack" }] },
    { num: "07", icon: Plug, title: "API & Systems Integration", short: "Stitch your stack together — securely.",
      desc: "Custom APIs, third-party integrations, ETL and real-time data sync. We connect your CRM, ERP, data warehouse and product so the right data lives in the right place at the right time.",
      pills: ["REST", "GraphQL", "Webhooks", "ETL", "CDC", "OAuth / mTLS"],
      kpi: [{ k: "99.99%", v: "Sync uptime" }, { k: "<200ms", v: "p95 latency" }] },
    { num: "08", icon: BarChart3, title: "Business Analytics & BI", short: "Dashboards that drive decisions.",
      desc: "Modern BI from instrumentation to insight — Power BI, Looker, Metabase — backed by an opinionated data warehouse so leadership decisions are grounded in numbers, not gut feel.",
      pills: ["Power BI", "Looker", "Metabase", "dbt", "BigQuery", "PostHog"],
      kpi: [{ k: "Live", v: "KPI dashboards" }, { k: "Daily", v: "Auto reports" }] },
    { num: "09", icon: Zap, title: "Workflow Automation", short: "Quiet, reliable automation behind the scenes.",
      desc: "Replace the boring, error-prone manual work with rock-solid pipelines. We build with n8n, Zapier, Make and custom code where it matters — with full observability and rollback baked in.",
      pills: ["n8n", "Zapier", "Make", "Custom Workers", "Cron", "Observability"],
      kpi: [{ k: "+25 hrs", v: "Saved/week" }, { k: "0", v: "Missed runs" }] },
    { num: "10", icon: Server, title: "Dedicated Servers & DevOps", short: "Production-grade infra with 99.9% uptime.",
      desc: "Managed cloud or bare-metal infrastructure with CI/CD pipelines, monitoring, hardened security and zero-downtime deploys. Sleep at night while we keep your systems humming.",
      pills: ["AWS", "GCP", "Kubernetes", "Docker", "CI/CD", "Monitoring"],
      kpi: [{ k: "99.9%", v: "Uptime SLA" }, { k: "24/7", v: "On-call" }] },
  ];

  const [active, setActive] = useState(0);
  const sel = items[active];
  const Icon = sel.icon;

  return (
    <section id="services" className="py-32 relative">
      <div className="wrap">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end reveal">
          <div className="lg:col-span-7">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: "var(--accent)" }}>What we do</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Ten services.
              <br />
              One <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>engineering</span> studio.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base sm:text-lg" style={{ color: "var(--ink-mute)" }}>
              From AI agents to dedicated servers, marketing to analytics — we ship
              the entire digital stack under one roof. One point of contact, no handoffs.
            </p>
          </div>
        </div>

        {/* Premium split: featured panel + selector list */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 reveal">
          {/* Featured Panel */}
          <div className="lg:col-span-7 service-feature relative overflow-hidden rounded-2xl"
               data-testid={`service-feature-${active}`}>
            <div className="service-feature-mesh" />
            <div className="relative p-8 sm:p-12">
              <div className="flex items-start justify-between mb-10 gap-4">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                     style={{ background: "linear-gradient(135deg, var(--accent), var(--accent-2))", color: "white", boxShadow: "0 16px 40px -10px var(--glow)" }}>
                  <Icon size={28} />
                </div>
                <span className="font-mono text-xs px-3 py-1.5 rounded-full"
                      style={{ background: "var(--surface-strong)", border: "1px solid var(--border-strong)", color: "var(--ink-soft)" }}>
                  {sel.num} / 10
                </span>
              </div>

              <h3 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight" data-testid="service-title">
                {sel.title}
              </h3>
              <p className="text-base sm:text-lg italic font-serif mb-5" style={{ color: "var(--ink-soft)" }}>
                {sel.short}
              </p>
              <p className="text-sm sm:text-base leading-relaxed mb-8" style={{ color: "var(--ink-mute)" }}>
                {sel.desc}
              </p>

              <div className="flex flex-wrap gap-2 mb-10">
                {sel.pills.map(p => <span key={p} className="pill">{p}</span>)}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-10">
                {sel.kpi.map(k => (
                  <div key={k.v} className="p-5 rounded-xl"
                       style={{ background: "var(--surface-strong)", border: "1px solid var(--border)" }}>
                    <div className="text-3xl font-bold gradient-text mb-1">{k.k}</div>
                    <div className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>{k.v}</div>
                  </div>
                ))}
              </div>

              <a href="#contact" className="btn-primary" data-testid="service-cta">
                Start a project <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* Selector List */}
          <div className="lg:col-span-5 space-y-1.5">
            {items.map((it, i) => {
              const isActive = i === active;
              const Ic = it.icon;
              return (
                <button
                  key={it.title}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  className={`service-row w-full text-left ${isActive ? "is-active" : ""}`}
                  data-testid={`service-card-${i}`}
                >
                  <span className="service-row-num font-mono">{it.num}</span>
                  <span className="service-row-icon">
                    <Ic size={18} />
                  </span>
                  <span className="service-row-body">
                    <span className="service-row-title">{it.title}</span>
                    <span className="service-row-sub">{it.short}</span>
                  </span>
                  <ArrowRight size={16} className="service-row-arrow" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────── PORTFOLIO (carousel + click-to-open modal) ───────── */
const Portfolio = () => {
  const projects = [
    {
      title: "Doon Auto Glass", cat: "Automotive Services",
      img: "https://images.pexels.com/photos/15489246/pexels-photo-15489246.jpeg?auto=compress&cs=tinysrgb&w=1400",
      link: "https://doonautoglass.com/",
      year: "2025",
      summary: "A fast, conversion-led marketing site for Doon Valley's premium auto-glass service — built to capture quote requests from mobile-first audiences.",
      tech: ["React", "Tailwind", "Vercel", "Local SEO"],
      results: [{ k: "+187%", v: "Quote requests" }, { k: "<1.2s", v: "LCP" }]
    },
    {
      title: "Car Glass Body", cat: "Automotive Platform",
      img: "https://images.pexels.com/photos/10126667/pexels-photo-10126667.jpeg?auto=compress&cs=tinysrgb&w=1400",
      link: "https://www.carglassbody.com/",
      year: "2025",
      summary: "Multi-service automotive platform with a service catalog, lead capture funnel and tightly managed local-SEO presence.",
      tech: ["Next.js", "MongoDB", "Schema.org", "GMB"],
      results: [{ k: "Top 3", v: "Local pack" }, { k: "+3.4×", v: "Organic traffic" }]
    },
    {
      title: "Aman Jewellers", cat: "E-commerce",
      img: "https://images.pexels.com/photos/13325931/pexels-photo-13325931.jpeg?auto=compress&cs=tinysrgb&w=1400",
      link: "https://aman-jewellers.vercel.app/",
      year: "2025",
      summary: "An editorial e-commerce experience for a heritage jewellery brand — slow scroll, rich product storytelling, and clean conversion flow.",
      tech: ["React", "Stripe-ready", "Cloudinary", "Tailwind"],
      results: [{ k: "+62%", v: "Time on site" }, { k: "+2.1×", v: "Add-to-cart" }]
    },
    {
      title: "Sunshine School", cat: "Education Portal",
      img: "https://images.pexels.com/photos/5212329/pexels-photo-5212329.jpeg?auto=compress&cs=tinysrgb&w=1400",
      link: "https://sunshine-school-sepia.vercel.app/",
      year: "2024",
      summary: "Modern school portal with admissions enquiry, faculty profiles, gallery and notices — designed for parents browsing on phones.",
      tech: ["Next.js", "Vercel", "Forms", "Accessibility AA"],
      results: [{ k: "+74%", v: "Admissions enquiries" }, { k: "100", v: "Lighthouse a11y" }]
    },
    {
      title: "Rising Star Academy", cat: "Institution Website",
      img: "https://images.pexels.com/photos/8617981/pexels-photo-8617981.jpeg?auto=compress&cs=tinysrgb&w=1400",
      link: "https://rising-star-liart.vercel.app/",
      year: "2024",
      summary: "Brand and digital refresh for a sports academy — emphasis on programs, coaches and a frictionless enrolment journey.",
      tech: ["React", "Tailwind", "Motion", "Vercel"],
      results: [{ k: "+45%", v: "Enrolments" }, { k: "−38%", v: "Bounce rate" }]
    },
  ];

  const [active, setActive] = useState(null);
  const scrollRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector(".pf-card");
    const step = card ? card.offsetWidth + 20 : 360;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // ESC closes modal
  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [active]);

  const p = active !== null ? projects[active] : null;

  return (
    <section id="portfolio" className="py-24 sm:py-32">
      <div className="wrap">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 reveal">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--accent)" }}>Selected work</p>
            <h2 className="text-4xl sm:text-5xl font-bold">
              Recent <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>launches</span>.
            </h2>
          </div>
          <div className="flex items-end gap-4 justify-between md:justify-end">
            <p className="text-base max-w-sm" style={{ color: "var(--ink-mute)" }}>
              Tap any project to see the brief, the stack and the numbers.
            </p>
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              <button onClick={() => scrollBy(-1)} aria-label="Previous" className="pf-arrow" data-testid="portfolio-prev-btn">
                <ArrowRight size={18} style={{ transform: "rotate(180deg)" }} />
              </button>
              <button onClick={() => scrollBy(1)} aria-label="Next" className="pf-arrow" data-testid="portfolio-next-btn">
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="pf-track reveal" data-testid="portfolio-carousel">
          {projects.map((proj, i) => (
            <button
              key={proj.title}
              className="pf-card group"
              onClick={() => setActive(i)}
              data-testid={`portfolio-item-${i}`}
              aria-label={`View details for ${proj.title}`}
            >
              <div className="pf-card-img">
                <img src={proj.img} alt={proj.title} loading="lazy" />
                <div className="pf-card-shade" />
                <span className="pf-card-year">{proj.year}</span>
              </div>
              <div className="pf-card-body">
                <p className="text-[10px] uppercase tracking-[0.18em] mb-1.5 font-semibold" style={{ color: "var(--accent)" }}>{proj.cat}</p>
                <h3 className="text-xl font-semibold mb-2 leading-tight" style={{ color: "var(--ink)" }}>{proj.title}</h3>
                <p className="text-sm leading-relaxed mb-4 line-clamp-2" style={{ color: "var(--ink-mute)" }}>{proj.summary}</p>
                <span className="text-sm font-medium inline-flex items-center gap-1.5" style={{ color: "var(--accent)" }}>
                  View case study <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>
          ))}
          <div className="pf-edge" />
        </div>
      </div>

      {/* Modal */}
      {p && (
        <div className="pf-modal" onClick={() => setActive(null)} data-testid="portfolio-modal">
          <div className="pf-modal-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button className="pf-modal-close" onClick={() => setActive(null)} aria-label="Close" data-testid="portfolio-modal-close">
              <X size={20} />
            </button>
            <div className="pf-modal-image">
              <img src={p.img} alt={p.title} />
              <span className="pf-modal-cat">{p.cat} · {p.year}</span>
            </div>
            <div className="pf-modal-body">
              <h3 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight">{p.title}</h3>
              <p className="text-base sm:text-lg mb-6 leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                {p.summary}
              </p>

              <div className="grid grid-cols-2 gap-3 mb-7">
                {p.results.map(r => (
                  <div key={r.v} className="p-4 rounded-xl"
                       style={{ background: "var(--surface-strong)", border: "1px solid var(--border)" }}>
                    <div className="text-2xl font-bold gradient-text mb-0.5">{r.k}</div>
                    <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>{r.v}</div>
                  </div>
                ))}
              </div>

              <div className="mb-7">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2.5" style={{ color: "var(--ink-mute)" }}>Stack</div>
                <div className="flex flex-wrap gap-1.5">
                  {p.tech.map(t => <span key={t} className="pill">{t}</span>)}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href={p.link} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="portfolio-modal-visit-btn">
                  Visit live site <ExternalLink size={16} />
                </a>
                <a href="#contact" onClick={() => setActive(null)} className="btn-ghost">
                  Build something similar
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/* ───────── PARTNERS ───────── */
const Partners = () => {
  const partners = [
    "Doon Auto Glass", "Car Glass Body", "Aman Jewellers", "Sunshine School",
    "Rising Star", "TechWave", "BlueOrbit", "Northstar", "PixelForge", "OakRidge",
    "Vertex Co.", "LumeraAI",
  ];
  return (
    <section className="py-24" style={{ borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
      <div className="wrap">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.25em] mb-10 reveal" style={{ color: "var(--ink-mute)" }}>
          Trusted by businesses & institutions across India
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-8 reveal">
          {partners.map(p => (
            <div key={p} className="flex items-center justify-center h-14 text-base sm:text-lg font-semibold opacity-50 hover:opacity-100 transition-opacity"
                 style={{ color: "var(--ink)" }}>
              {p}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────── CAPABILITIES (Tech deep-dive) ───────── */
const Capabilities = () => {
  const groups = [
    { icon: Code2, title: "Frontend", desc: "Pixel-perfect, accessible interfaces engineered for speed and conversion.",
      pills: ["React", "Next.js", "TypeScript", "Tailwind", "Framer Motion", "Three.js"] },
    { icon: Database, title: "Backend & Data", desc: "APIs and data layers built for scale, with clean architecture and observability.",
      pills: ["Node.js", "Python", "FastAPI", "Express", "MongoDB", "PostgreSQL", "Redis"] },
    { icon: Brain, title: "AI / ML", desc: "Generative AI, agents, retrieval and fine-tuned models — production-ready.",
      pills: ["OpenAI", "Claude", "Gemini", "LangChain", "Vector DBs", "Whisper"] },
    { icon: Cloud, title: "Cloud & Infra", desc: "Edge-deployed, globally distributed, monitored 24/7 with auto-scaling baked in.",
      pills: ["AWS", "GCP", "Vercel", "Cloudflare", "Docker", "Kubernetes"] },
    { icon: Workflow, title: "Automation", desc: "Custom workflows that quietly do the boring work so your team can focus.",
      pills: ["n8n", "Zapier", "Make", "Cron jobs", "Webhooks", "AI Agents"] },
    { icon: Shield, title: "Security", desc: "Defense-in-depth from day one — encryption, audits, and battle-tested patterns.",
      pills: ["SSL", "OAuth 2.0", "JWT", "RBAC", "OWASP", "Rate-limiting"] },
    { icon: Smartphone, title: "Mobile", desc: "Native-feeling cross-platform apps that share code without compromise.",
      pills: ["React Native", "Expo", "Flutter", "PWA"] },
    { icon: BarChart3, title: "Analytics & BI", desc: "Insightful dashboards and instrumentation that turn data into decisions.",
      pills: ["Power BI", "Looker", "Metabase", "PostHog", "GA4"] },
    { icon: GitBranch, title: "DevOps & CI/CD", desc: "Ship safely, multiple times a day, with full rollback and incident telemetry.",
      pills: ["GitHub Actions", "GitLab CI", "Terraform", "Sentry", "Prometheus"] },
  ];

  return (
    <section id="capabilities" className="py-32">
      <div className="wrap">
        <div className="text-center mb-16 reveal">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--accent)" }}>Capabilities</p>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            A full-stack <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>toolkit</span>.
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: "var(--ink-mute)" }}>
            From the first pixel to production traffic — here&apos;s the technology we wield to build software that lasts.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {groups.map((g, i) => (
            <div key={g.title} className="tile card-hover reveal" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`capability-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "var(--surface-strong)", border: "1px solid var(--border-strong)", color: "var(--accent)" }}>
                  <g.icon size={20} />
                </div>
                <h3 className="text-lg font-semibold">{g.title}</h3>
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--ink-mute)" }}>{g.desc}</p>
              <div className="flex flex-wrap gap-2">
                {g.pills.map(p => <span key={p} className="pill">{p}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────── PROCESS ───────── */
const Process = () => {
  const steps = [
    { icon: Search, num: "01", t: "Discovery", d: "We map your goals, audience, constraints and competitive landscape — then translate that into a sharp project brief." },
    { icon: PenTool, num: "02", t: "Design", d: "Wireframes, motion, brand language and interaction prototypes — reviewed weekly, never thrown over a wall." },
    { icon: Hammer, num: "03", t: "Engineering", d: "Component-driven builds with previews on every commit. Code is reviewed, tested, and documented as we go." },
    { icon: Rocket, num: "04", t: "Launch & Care", d: "Zero-downtime deploys, performance audits, and a 30-day care window — followed by ongoing support if you want it." },
  ];

  return (
    <section id="process" className="py-32">
      <div className="wrap">
        <div className="text-center mb-20 reveal">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--accent)" }}>The process</p>
          <h2 className="text-4xl sm:text-5xl font-bold">
            How we <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>ship</span>.
          </h2>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-2 bottom-2 w-px timeline-rail" />
          <div className="space-y-10 lg:space-y-20">
            {steps.map((s, i) => (
              <div key={s.num}
                   className={`reveal lg:w-1/2 ${i % 2 === 0 ? "lg:pr-12 lg:text-right" : "lg:pl-12 lg:ml-auto"}`}
                   data-testid={`process-step-${i}`}>
                <div className="tile relative">
                  <div className={`hidden lg:flex absolute top-6 ${i % 2 === 0 ? "-right-[33px]" : "-left-[33px]"} w-4 h-4 rounded-full items-center justify-center pulse-dot`}
                       style={{ background: "var(--accent)" }} />
                  <div className={`flex items-center gap-3 mb-4 ${i % 2 === 0 ? "lg:justify-end" : ""}`}>
                    <span className="font-mono text-sm" style={{ color: "var(--accent)" }}>{s.num}</span>
                    <span className="font-semibold text-xl">{s.t}</span>
                    <s.icon size={20} style={{ color: "var(--accent-2)" }} />
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--ink-mute)" }}>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────── TESTIMONIALS ───────── */
const Testimonials = () => {
  const quotes = [
    {
      quote: "The team brought our vision to life with a seamless, beautifully crafted web app. Communication was sharp, delivery was on time, and the polish was beyond what we expected.",
      name: "Aman Verma", role: "Founder, Aman Jewellers", tag: "E-commerce"
    },
    {
      quote: "Outstanding technical depth and great pricing. They didn't just build what we asked — they advised us on scaling, security and SEO from day one.",
      name: "Rohit Sharma", role: "Director, Doon Auto Glass", tag: "Automotive"
    },
    {
      quote: "We've worked with three agencies before Next-Gen. None came close on quality, speed, or follow-through. Genuine engineering partners.",
      name: "Priya Mehta", role: "Principal, Sunshine School", tag: "Education"
    },
    {
      quote: "Their automation workflows saved us ~25 hours/week of manual ops within the first month. ROI was instant.",
      name: "Vikram Singh", role: "Operations, Car Glass Body", tag: "Automotive"
    },
  ];

  return (
    <section className="py-32">
      <div className="wrap">
        <div className="text-center mb-16 reveal">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--accent)" }}>Elevating trust</p>
          <h2 className="text-4xl sm:text-5xl font-bold">
            Words from the <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>people who matter</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {quotes.map((q, i) => (
            <div key={q.name} className="tile card-hover reveal" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`testimonial-${i}`}>
              <Quote size={28} style={{ color: "var(--accent)", opacity: 0.5 }} className="mb-4" />
              <p className="text-base sm:text-lg leading-relaxed mb-6" style={{ color: "var(--ink-soft)" }}>
                &ldquo;{q.quote}&rdquo;
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold" style={{ color: "var(--ink)" }}>{q.name}</div>
                  <div className="text-sm" style={{ color: "var(--ink-mute)" }}>{q.role}</div>
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={14} style={{ color: "var(--accent-2)", fill: "var(--accent-2)" }} />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────── FAQ ───────── */
const FAQ = () => {
  const faqs = [
    { q: "What does Next-Gen Web Solutions do?",
      a: "We're a full-stack engineering studio. We design and build websites, web & mobile applications, AI products, automation workflows, dedicated server infrastructure, and run performance marketing & SEO — all under one roof." },
    { q: "Do you provide custom software development?",
      a: "Yes. We replace fragile manual processes with bespoke software — built to your specs, with clean code, good documentation, and a scalable architecture you actually own." },
    { q: "What AI & machine learning solutions do you offer?",
      a: "Generative AI integration, custom chatbots, AI agents, NLP, computer vision and analytics. We help you choose the right model (OpenAI, Claude, Gemini, open-source) and deploy it responsibly in production." },
    { q: "Do you work with startups or enterprises?",
      a: "Both. We work with funded startups that need to move fast without rebuilds, and with established teams that need senior engineering muscle on a specific initiative." },
    { q: "Will we own the source code and IP?",
      a: "Always. 100% of the source code and intellectual property belongs to you, with no vendor lock-in. We hand off clean repositories and full documentation." },
    { q: "How long does a typical project take?",
      a: "A focused marketing site ships in 2–3 weeks. A SaaS MVP usually takes 6–10 weeks. Enterprise platforms run 3–6 months. We always start with a fixed-scope discovery sprint to nail down a realistic timeline." },
    { q: "Can you integrate with our existing tools and systems?",
      a: "Yes — we specialize in API integrations, data sync and migrating legacy systems without breaking running operations." },
    { q: "Do you provide post-launch support?",
      a: "Yes. Every project ships with a 30-day care window included. Monthly retainers, SLA-backed support and feature pods are available afterwards." },
  ];

  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="faq" className="py-32">
      <div className="wrap">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 reveal">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--accent)" }}>FAQ</p>
            <h2 className="text-4xl sm:text-5xl font-bold mb-5">
              Have <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>questions</span>?
            </h2>
            <p className="text-base mb-6" style={{ color: "var(--ink-mute)" }}>
              The most common things people ask before kicking off a project.
              Don&apos;t see yours? Drop us a line and we&apos;ll respond personally.
            </p>
            <a href="#contact" className="btn-ghost text-sm">
              Ask us anything <ArrowRight size={16} />
            </a>
          </div>

          <div className="lg:col-span-3 space-y-3 reveal">
            {faqs.map((f, i) => {
              const open = openIdx === i;
              return (
                <div key={f.q} className="tile" style={{ padding: 0 }} data-testid={`faq-item-${i}`}>
                  <button
                    className="w-full flex items-center justify-between p-5 text-left"
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    aria-expanded={open}
                  >
                    <span className="text-base sm:text-lg font-semibold pr-4" style={{ color: "var(--ink)" }}>{f.q}</span>
                    <ChevronDown size={20} style={{ color: "var(--accent)", transform: open ? "rotate(180deg)" : "none", transition: "transform .3s" }} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 -mt-1 text-sm leading-relaxed" style={{ color: "var(--ink-mute)" }}>
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────── CONTACT ───────── */
const makeCaptcha = () => ({
  a: Math.floor(Math.random() * 9) + 1,
  b: Math.floor(Math.random() * 9) + 1,
  ans: "",
});

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [captcha, setCaptcha] = useState(makeCaptcha);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const newCaptcha = useCallback(() => setCaptcha(makeCaptcha()), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (parseInt(captcha.ans) !== captcha.a + captcha.b) {
      setStatus({ type: "err", msg: "Captcha answer is incorrect. Please try again." });
      newCaptcha();
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email, company: form.company, message: form.message,
          captcha_answer: parseInt(captcha.ans), captcha_expected: captcha.a + captcha.b,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      setStatus({ type: "ok", msg: "Message received — we'll get back to you within 24 hours." });
      setForm({ name: "", email: "", company: "", message: "" });
      newCaptcha();
    } catch (err) {
      setStatus({ type: "err", msg: err.message || "Something went wrong. Try emailing us directly." });
      newCaptcha();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="py-32">
      <div className="wrap">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2 reveal">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--accent)" }}>Get in touch</p>
            <h2 className="text-4xl sm:text-5xl font-bold mb-5">
              Let&apos;s build something <span className="font-serif gradient-text" style={{ fontWeight: 400 }}>exceptional</span>.
            </h2>
            <p className="text-base mb-10" style={{ color: "var(--ink-soft)" }}>
              Tell us about your project — we&apos;ll respond within 24 hours with a clear next step.
              No bots, no funnels.
            </p>

            <div className="space-y-5">
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 group" data-testid="contact-email-link">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "var(--surface-strong)", border: "1px solid var(--border-strong)", color: "var(--accent)" }}>
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>Email</div>
                  <div className="font-medium group-hover:text-[var(--accent)] transition-colors" style={{ color: "var(--ink)" }}>{EMAIL}</div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "var(--surface-strong)", border: "1px solid var(--border-strong)", color: "var(--accent)" }}>
                  <Globe size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>Website</div>
                  <div className="font-medium" style={{ color: "var(--ink)" }}>{DOMAIN}</div>
                </div>
              </div>

              <a href={`tel:${PHONE}`} className="flex items-center gap-4 group" data-testid="contact-phone-link">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "var(--surface-strong)", border: "1px solid var(--border-strong)", color: "var(--accent)" }}>
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>Call · WhatsApp</div>
                  <div className="font-medium group-hover:text-[var(--accent)] transition-colors" style={{ color: "var(--ink)" }}>{PHONE_DISPLAY}</div>
                </div>
              </a>

              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp-link">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "linear-gradient(135deg, #25D366, #128C7E)", color: "white" }}>
                  <MessageCircle size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>WhatsApp chat</div>
                  <div className="font-medium group-hover:text-[var(--accent)] transition-colors" style={{ color: "var(--ink)" }}>Start a conversation</div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "var(--surface-strong)", border: "1px solid var(--border-strong)", color: "var(--accent)" }}>
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>Response time</div>
                  <div className="font-medium" style={{ color: "var(--ink)" }}>Within 24 hours</div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-3 tile reveal" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: "var(--ink-mute)" }}>Name *</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                       className="fld" placeholder="Your name" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: "var(--ink-mute)" }}>Email *</label>
                <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                       className="fld" placeholder="you@company.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: "var(--ink-mute)" }}>Company</label>
              <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                     className="fld" placeholder="Optional" data-testid="contact-company-input" />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-medium mb-2 uppercase tracking-wider" style={{ color: "var(--ink-mute)" }}>Project Brief *</label>
              <textarea required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                        rows={5} className="fld resize-none" placeholder="What are you building?" data-testid="contact-message-input" />
            </div>

            <div className="mb-5 p-4 rounded-xl" style={{ background: "var(--surface-strong)", border: "1px solid var(--border)" }}>
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--ink)" }}>
                Quick check: what is <span className="font-mono">{captcha.a} + {captcha.b}</span>? *
              </label>
              <div className="flex items-center gap-3">
                <input required type="number" value={captcha.ans} onChange={e => setCaptcha({ ...captcha, ans: e.target.value })}
                       className="fld" style={{ width: 120 }} placeholder="Answer" data-testid="contact-captcha-input" />
                <button type="button" onClick={newCaptcha} className="text-xs font-medium underline" style={{ color: "var(--ink-mute)" }}>
                  New question
                </button>
              </div>
            </div>

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="contact-submit-btn">
              {busy ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Sending…
                </span>
              ) : (
                <>Send message <Send size={16} /></>
              )}
            </button>

            {status && (
              <p className="mt-4 text-sm" style={{ color: status.type === "ok" ? "#22c55e" : "#ef4444" }} data-testid="contact-status-msg">
                {status.msg}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

/* ───────── FLOATING WHATSAPP ───────── */
const WhatsAppFloat = () => (
  <a
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="wa-float"
    aria-label="Chat with NextGen on WhatsApp"
    data-testid="whatsapp-float-btn"
  >
    <span className="wa-pulse" />
    <svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor" aria-hidden="true">
      <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 01-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 01-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.04 2.264v.114c-.015.99.472 1.977 1.117 2.78 1.39 1.704 3.123 3.045 5.09 3.886.515.214 2.306 1.05 2.97.745.6-.286 1.118-.99 1.318-1.677.087-.302.06-.5.045-.61-.06-.214-.21-.343-.41-.444-.187-.114-.74-.487-.913-.487zm-3.116-13.166a12.945 12.945 0 0112.948 12.96 12.953 12.953 0 01-19.815 10.945L2 30l2.273-6.802A12.96 12.96 0 0115.993 4.039zm0 2.275c-5.876 0-10.685 4.81-10.685 10.685 0 2.034.587 4.025 1.693 5.74L5.27 26.945l4.39-1.69a10.61 10.61 0 006.334 2.062c5.876 0 10.685-4.81 10.685-10.685s-4.81-10.32-10.685-10.32z"/>
    </svg>
    <span className="wa-label">Chat with us</span>
  </a>
);

/* ───────── FOOTER ───────── */
const Footer = () => (
  <footer className="pt-24 pb-10 relative overflow-hidden">
    <div className="absolute inset-x-0 bottom-0 flex items-end justify-center pointer-events-none">
      <span className="watermark">{BRAND.toUpperCase()}</span>
    </div>
    <div className="wrap relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-10 mb-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <Logo size={38} />
            <span className="font-semibold text-lg">{BRAND_FULL}</span>
          </div>
          <p className="text-sm max-w-sm" style={{ color: "var(--ink-mute)" }}>
            Building elite digital infrastructure for modern businesses, institutions and ambitious founders.
          </p>
          <div className="flex items-center gap-2 mt-5 text-sm" style={{ color: "var(--ink-soft)" }}>
            <CheckCircle2 size={16} style={{ color: "var(--accent)" }} />
            Premium technology partner
          </div>

          <div className="flex items-center gap-3 mt-6" data-testid="footer-socials">
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer"
               className="social-btn" aria-label="NextGen on LinkedIn" data-testid="social-linkedin">
              <Linkedin size={18} />
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
               className="social-btn" aria-label="NextGen on Instagram" data-testid="social-instagram">
              <Instagram size={18} />
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
               className="social-btn social-wa" aria-label="NextGen on WhatsApp" data-testid="social-whatsapp">
              <MessageCircle size={18} />
            </a>
            <a href={`mailto:${EMAIL}`}
               className="social-btn" aria-label="Email NextGen" data-testid="social-email">
              <Mail size={18} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-4 text-sm uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>Services</h4>
          <ul className="space-y-2.5 text-sm">
            {["AI Development", "Web & Mobile", "Automation", "Performance Marketing", "SEO & GMB"].map(l => (
              <li key={l}><a href="#services" style={{ color: "var(--ink-soft)" }}>{l}</a></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4 text-sm uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>Studio</h4>
          <ul className="space-y-2.5 text-sm">
            <li><a href="#capabilities" style={{ color: "var(--ink-soft)" }}>Capabilities</a></li>
            <li><a href="#portfolio" style={{ color: "var(--ink-soft)" }}>Portfolio</a></li>
            <li><a href="#process" style={{ color: "var(--ink-soft)" }}>Process</a></li>
            <li><a href="#faq" style={{ color: "var(--ink-soft)" }}>FAQ</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4 text-sm uppercase tracking-widest" style={{ color: "var(--ink-mute)" }}>Contact</h4>
          <ul className="space-y-2.5 text-sm">
            <li><a href={`mailto:${EMAIL}`} style={{ color: "var(--ink-soft)" }} data-testid="footer-email">{EMAIL}</a></li>
            <li><a href={`tel:${PHONE}`} style={{ color: "var(--ink-soft)" }} data-testid="footer-phone">{PHONE_DISPLAY}</a></li>
            <li><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink-soft)" }} data-testid="footer-whatsapp">WhatsApp us</a></li>
            <li><a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink-soft)" }} data-testid="footer-linkedin">LinkedIn</a></li>
            <li><a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink-soft)" }} data-testid="footer-instagram">Instagram</a></li>
            <li style={{ color: "var(--ink-soft)" }}>{DOMAIN}</li>
          </ul>
        </div>
      </div>

      <div style={{ height: 1, background: "var(--border)" }} />

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8">
        <p className="text-xs" style={{ color: "var(--ink-mute)" }}>
          © {new Date().getFullYear()} {BRAND_FULL} · {DOMAIN}
        </p>
        <a href={`tel:${PHONE}`} className="text-xs font-medium" style={{ color: "var(--ink-soft)" }} data-testid="footer-bottom-phone">
          {PHONE_DISPLAY}
        </a>
      </div>
    </div>
  </footer>
);

/* ───────── APP ───────── */
export default function App() {
  const { theme, toggle } = useTheme();
  useReveal();

  return (
    <>
      <Sky />
      <Nav theme={theme} toggle={toggle} />
      <main>
        <Hero />
        <Trust />
        <Services />
        <Portfolio />
        <Partners />
        <Capabilities />
        <Process />
        <Testimonials />
        <FAQ />
        <Contact />
      </main>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}
